import { memo } from "react";

import { Box, TableCell } from "@mui/material";

import { ConditionalLink } from "../buttons";
import { Checkbox } from "../input";
import { BODY_CELL_SX, LEADING_COL_WIDTH, RowDivider, StyledTableRow, colFlexAlign, getCellInnerSx } from "./cell";
import { RowCheckboxState } from "./useSelection";
import { TABLE_CTA_COLUMN_KEY, TableColumn, TableRow } from "./types";

interface RowProps<TRow> {
    row: TableRow<TRow>;
    rowIndex: number;
    columns: TableColumn<TRow>[];
    /** Pre-computed by useSelection. Present iff selection is enabled. */
    checkbox?: RowCheckboxState;
    onRowClick?: (row: TableRow<TRow>) => void;
    onRowNavigatePath?: (row: TableRow<TRow>) => string | undefined;
    isDisabled?: boolean;
    divider: string;
    hoverBackground: string;
}

function RowImpl<TRow>({
    row,
    rowIndex,
    columns,
    checkbox,
    onRowClick,
    onRowNavigatePath,
    isDisabled,
    divider,
    hoverBackground
}: RowProps<TRow>) {
    const hasCheckbox = !!checkbox;
    const leadingCells = hasCheckbox ? 1 : 0;
    const totalColSpan = columns.length + leadingCells;

    const navigatePath = onRowNavigatePath?.(row);
    const isLinkRow = !!navigatePath && !isDisabled;
    const isClickable = !isDisabled && (!!onRowClick || isLinkRow);

    // Hover via CSS — replaces the previous design's useState-driven hover that
    // caused a React re-render on every mouseenter/leave per row.
    const hoverSx = isClickable ? { cursor: "pointer", "&:hover": { backgroundColor: hoverBackground } } : {};

    return (
        <StyledTableRow
            sx={{
                position: "relative",
                opacity: isDisabled ? 0.4 : 1,
                filter: isDisabled ? "saturate(0)" : undefined,
                pointerEvents: isDisabled ? "none" : "auto",
                ...hoverSx
            }}
        >
            {hasCheckbox && (
                <TableCell sx={{ ...BODY_CELL_SX, width: LEADING_COL_WIDTH }}>
                    <RowDivider color={divider} />

                    <Box
                        sx={{
                            ...getCellInnerSx({
                                colIdx: 0,
                                totalColSpan,
                                isGrouped: false,
                                hasCheckbox: true
                            }),
                            gap: 0
                        }}
                    >
                        <span onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                                checked={checkbox!.checked}
                                indeterminate={checkbox!.indeterminate}
                                onChange={checkbox!.onToggle}
                            />
                        </span>
                    </Box>
                </TableCell>
            )}
            {columns.map((col, j) => {
                const colIdx = j + leadingCells;
                const isCta = !isDisabled && col.dataIndex === TABLE_CTA_COLUMN_KEY;
                const to = isCta || !isLinkRow ? undefined : navigatePath;
                return (
                    <TableCell
                        key={col.dataIndex}
                        align={col.align ?? "left"}
                        sx={{ ...BODY_CELL_SX, ...{ minWidth: col.width ?? "auto", maxWidth: col.width ?? "auto" } }}
                        onClick={() => {
                            if (isCta || isDisabled) return;
                            onRowClick?.(row);
                        }}
                    >
                        {/* RowDivider is rendered once per row via the first cell to keep the
                            DOM count low — but visually the line spans the full row because
                            it's absolutely positioned to the row's container. We render it
                            inside the first data cell when there's no checkbox cell. */}
                        {!hasCheckbox && j === 0 && <RowDivider color={divider} />}
                        <ConditionalLink
                            to={to}
                            disabled={isDisabled}
                            sx={{
                                ...getCellInnerSx({
                                    colIdx,
                                    totalColSpan,
                                    isGrouped: false,
                                    hasCheckbox
                                }),
                                justifyContent: colFlexAlign(col)
                            }}
                        >
                            {col.render(row, rowIndex)}
                        </ConditionalLink>
                    </TableCell>
                );
            })}
        </StyledTableRow>
    );
}

// React.memo with default shallow compare — closes the "click an unrelated row,
// every other row re-renders" hole once consumers stabilize their handlers.
export const Row = memo(RowImpl) as <TRow>(props: RowProps<TRow>) => JSX.Element;
